import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveProject } from '../context/ActiveProjectContext';
import {
  LayoutDashboard,
  BriefcaseBusiness,
  ChartColumnBig,
  ListOrdered,
  Settings2,
  Building2,
  Users,
  Mail,
  User,
  LogOut,
  Menu,
  X,
  Wallet,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  CheckCircle2,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/dashboard',    label: 'Financial Overview', Icon: BriefcaseBusiness, alwaysVisible: true },
  { to: '/organization/dashboard', label: 'Organization', Icon: Building2, alwaysVisible: true },
  { to: '/organization/members', label: 'Members', Icon: Users, anyPermissions: ['view_users'] },
  { to: '/organization/invitations', label: 'Invitations', Icon: Mail, anyPermissions: ['view_users', 'invite_users'] },
  { to: '/organization/permissions', label: 'Permissions', Icon: Settings2, anyPermissions: ['manage_role_permissions'] },
  { to: '/transactions', label: 'Transactions', Icon: ListOrdered, anyPermissions: ['view_transactions'] },
  { to: '/management', label: 'Categories & Persons', Icon: Settings2, anyPermissions: ['manage_categories', 'manage_persons'] },
  { to: '/history-analytics', label: 'History Analytics', Icon: ChartColumnBig, anyPermissions: ['view_project_history', 'view_analytics'] },
  { to: '/organization/settings', label: 'Org Settings', Icon: LayoutDashboard, anyPermissions: ['manage_organization_settings'] },
  { to: '/profile',      label: 'Profile',      Icon: User, alwaysVisible: true },
];

const ROLE_NAV_MAP = {
  organization_head: [
    '/dashboard',
    '/organization/dashboard',
    '/organization/members',
    '/organization/invitations',
    '/organization/permissions',
    '/transactions',
    '/management',
    '/history-analytics',
    '/organization/settings',
    '/profile',
  ],
  admin: [
    '/dashboard',
    '/organization/dashboard',
    '/organization/members',
    '/organization/invitations',
    '/organization/permissions',
    '/transactions',
    '/management',
    '/history-analytics',
    '/organization/settings',
    '/profile',
  ],
  finance_manager: ['/finance', '/transactions', '/history-analytics', '/organization/dashboard', '/profile'],
  accountant: ['/accountant', '/transactions', '/management', '/history-analytics', '/profile'],
  project_manager: ['/project-manager', '/dashboard', '/transactions', '/history-analytics', '/profile'],
  employee: ['/dashboard', '/transactions', '/history-analytics', '/profile'],
  viewer: ['/viewer', '/viewer/reports', '/history-analytics', '/profile'],
};

const PRIMARY = '#c44569';
const PRIMARY_GRADIENT = 'linear-gradient(135deg,#c44569,#cf6a87)';

/* ── Project Switcher Dropdown ── */
const ProjectSwitcher = () => {
  const { projects, selectedProject, selectProject, openProjectPicker, loading } = useActiveProject();
  const { effectiveRole } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (effectiveRole === 'viewer') return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition max-w-[200px]"
        style={{ borderColor: '#f4d9e1', background: '#fff8fa', color: '#c44569' }}
      >
        {selectedProject ? (
          <>
            <CheckCircle2 size={13} color="#16a34a" />
            <span className="truncate max-w-[120px]">{selectedProject.name}</span>
          </>
        ) : (
          <>
            <FolderOpen size={13} />
            <span>Select Project</span>
          </>
        )}
        <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-64 bg-white rounded-2xl shadow-xl border overflow-hidden z-50"
          style={{ borderColor: '#f4d9e1' }}>
          <div className="px-3 py-2 border-b text-xs font-semibold text-slate-500 uppercase tracking-wider"
            style={{ borderColor: '#f9edf0' }}>
            Switch Project
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {loading ? (
              <div className="px-4 py-3 text-xs text-slate-400">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400">No projects yet</div>
            ) : projects.map((p) => {
              const active = String(selectedProject?.id || '') === String(p.id);
              return (
                <button key={p.id} type="button"
                  onClick={async () => { await selectProject(p); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium text-left transition hover:bg-rose-50"
                  style={{ color: active ? '#c44569' : '#334155' }}
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: active ? '#fce0e8' : '#f1f5f9' }}>
                    {active
                      ? <CheckCircle2 size={12} color="#c44569" />
                      : <FolderOpen size={11} color="#94a3b8" />}
                  </div>
                  <span className="truncate flex-1">{p.name}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t px-3 py-2" style={{ borderColor: '#f9edf0' }}>
            <button type="button"
              onClick={() => { openProjectPicker(); setOpen(false); }}
              className="w-full text-xs font-semibold py-1.5 rounded-lg transition text-white"
              style={{ background: PRIMARY }}>
              Manage Projects
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Drawer = ({ pageHeader }) => {
  const { user, logout, effectiveRole, effectiveRoleLabel, hasAnyPermission, permissionSource } = useAuth();
  const { selectedProject } = useActiveProject();
  const navigate         = useNavigate();
  const [sidebarOpen, setSidebarOpen]  = useState(true);
  const sidebarW = 260;

  /* Close drawer on Escape */
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') setSidebarOpen(false);
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const allowedPaths = ROLE_NAV_MAP[effectiveRole] || ROLE_NAV_MAP.employee;
  const roleNavItems = NAV_ITEMS.filter((item) => {
    if (!allowedPaths.includes(item.to)) return false;
    if (item.alwaysVisible) return true;
    if (!item.anyPermissions || item.anyPermissions.length === 0) return true;
    return hasAnyPermission(item.anyPermissions);
  });

  return (
    <div className="min-h-screen max-h-screen flex" style={{ background: '#f8fafc' }}>
      {/* ── Overlay (mobile only) ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(30,10,18,0.45)', backdropFilter: 'blur(2px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
          className="fixed top-0 left-0 h-screen z-50 lg:static lg:z-auto flex flex-col transition-all duration-300 ems-card-elevated"
        style={{
          width: sidebarW,
          background: '#fff',
            boxShadow: '2px 0 18px rgba(15,23,42,0.09)',
          transform: sidebarOpen ? 'translateX(0)' : `translateX(-${sidebarW}px)`,
        }}
      >
        {/* Professional Logo Header */}
        <div
          className="flex items-center justify-between px-5 py-6"
          style={{background: PRIMARY_GRADIENT}}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{background:'rgba(255,255,255,0.25)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.4)'}}>
              <Wallet size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-base leading-tight tracking-tighter">EMS</p>
              <p className="text-white/80 text-xs font-medium">Expense Manager</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center transition"
            style={{background:'rgba(255,255,255,0.15)',color:'#fff'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.25)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.15)'}
            aria-label="Close navigation"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* User card */}
        <div className="mx-4 mt-4 mb-2 rounded-2xl px-4 py-3 flex items-center gap-3" style={{background:'#fff5f8',border:'1.5px solid #f8d3dd'}}>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0"
            style={{background: PRIMARY_GRADIENT,color:'#fff'}}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <p
              className="text-[11px] mt-1 inline-flex items-center px-2 py-0.5 rounded-full font-semibold"
              style={{ color: '#7a1f3d', background: '#fce0e8' }}
            >
              {effectiveRoleLabel}
            </p>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-widest px-3 py-2" style={{color: PRIMARY,opacity:0.6}}>Navigation</p>
          {roleNavItems.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all group ${
                  isActive ? 'text-white shadow-md' : 'text-slate-600 hover:text-slate-800'
                }`
              }
              style={({ isActive }) => isActive
                ? {background: PRIMARY_GRADIENT, boxShadow:'0 4px 12px rgba(196,69,105,0.25)'}
                : {}}
            >
              {({ isActive }) => (
                <>
                  <Icon size={17} strokeWidth={isActive ? 2.5 : 2.2} />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{label}</span>
                    {(() => {
                      const navItem = NAV_ITEMS.find((item) => item.to === to);
                      if (!navItem?.anyPermissions?.length) return null;
                      const source = permissionSource(navItem.anyPermissions[0]);
                      if (!source || source === 'System Default Permission') return null;
                      const isOrgHead = source.toLowerCase().includes('head');
                      const isAdmin = source.toLowerCase().includes('admin');
                      return (
                        <span
                          className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-bold mt-0.5"
                          style={isOrgHead
                            ? { background: 'rgba(255,220,235,0.9)', color: '#9d174d' }
                            : isAdmin
                              ? { background: 'rgba(219,234,254,0.9)', color: '#1e40af' }
                              : null}
                        >
                          {isOrgHead ? 'by Org Head' : 'by Admin'}
                        </span>
                      );
                    })()}
                  </span>
                  {isActive && <ChevronRight size={14} strokeWidth={2.5} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 pb-6">
          <div className="border-t mb-4" style={{borderColor:'#fce0e8'}} />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{background:'#fee2e2',color:'#dc2626'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#fecaca'}}
            onMouseLeave={e=>{e.currentTarget.style.background='#fee2e2'}}
          >
            <LogOut size={17} strokeWidth={2.2} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col h-screen">
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between px-4 h-14 shrink-0"
          style={{background:'#fff',borderBottom:'1.5px solid #fce0e8',boxShadow:'0 1px 8px rgba(196,69,105,0.07)'}}
        >
          {/* Hamburger toggle */}
          <button
            onClick={() => setSidebarOpen(s => !s)}
            className="flex items-center justify-center w-9 h-9 rounded-xl transition"
            style={{background:'#fdf0f4',color: PRIMARY}}
            onMouseEnter={e=>e.currentTarget.style.background='#fce0e8'}
            onMouseLeave={e=>e.currentTarget.style.background='#fdf0f4'}
            aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
          >
            {sidebarOpen ? <X size={19} strokeWidth={2.2} /> : <Menu size={19} strokeWidth={2.2} />}
          </button>

          <div className="flex items-center gap-2">
            <ProjectSwitcher />
          </div>

          {/* Brand — right side */}
          <div className="flex items-center gap-2">
            <span className="text-base font-extrabold tracking-tight" style={{color: PRIMARY}}>EMS</span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shadow" style={{background: PRIMARY}}>
              <Wallet size={14} color="#fff" strokeWidth={2.5} />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-slate-50">
          {pageHeader}
        </main>
      </div>
    </div>
  );
};

export default Drawer;
